import type { IOrganizationGroupListItem } from "@/api/organization-groups";
import { DataTable } from "@/components/shared/DataTable";
import { AlertCircle, Network, SearchX } from "lucide-react";
import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import { columns } from "./columns";

interface GroupsTableProps {
    data: IOrganizationGroupListItem[];
    rowCount?: number;
    isLoading?: boolean;
    isError?: boolean;
    isRecoveringPage?: boolean;
    hasSearch?: boolean;
    onClearSearch?: () => void;
    onRetry?: () => void;
    pagination: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
}

export function GroupsTable({
    data,
    rowCount,
    isLoading,
    isError,
    isRecoveringPage = false,
    hasSearch,
    onClearSearch,
    onRetry,
    pagination,
    onPaginationChange,
}: GroupsTableProps) {
    return (
        <DataTable
            data={data}
            columns={columns}
            entityLabel="groups"
            rowCount={rowCount}
            isLoading={isLoading}
            isError={isError}
            isRecovering={isRecoveringPage}
            hasActiveFilters={hasSearch}
            onClearFilters={onClearSearch}
            clearFiltersLabel="Clear search"
            onRetry={onRetry}
            pagination={pagination}
            onPaginationChange={onPaginationChange}
            getRowId={(row) => String(row.id)}
            errorState={{
                icon: (
                    <AlertCircle className="text-muted-foreground/50 size-10" />
                ),
                title: "Couldn't load groups",
                body: "Something went wrong. Please try again.",
            }}
            recoveringState={{
                icon: <Network className="text-muted-foreground/50 size-10" />,
                title: "Loading groups",
                body: "Moving to the last available page.",
            }}
            filteredState={{
                icon: <SearchX className="text-muted-foreground/50 size-10" />,
                title: "No groups match your search",
                body: "Try a different name.",
            }}
            emptyState={{
                icon: <Network className="text-muted-foreground/50 size-10" />,
                title: "No groups yet",
                body: "Create a group to share a knowledge base between organizations.",
            }}
        />
    );
}
